import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  GlobalStyles,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import { getTrackersApi } from "src/DAL/tracker/tracker";
import {
  discardQuestionDraftApi,
  getQuestionsApi,
  publishQuestionsApi,
  saveQuestionDraftApi,
} from "src/DAL/coachWorkspace/coachWorkspace";
import EditFormFieldsDialog from "src/pages/Trackers/EditFormFieldsDialog";
import { Breadcrumbs, CW, ErrorBox, InfoHint, Loading, PageTitle, Panel, Pill, TextLink, UnderlineTabs } from "./cwUi";

/**
 * Admin copy of the coach portal page (NEW admin path /check-ins/:trackerId/questions).
 * P5.1 / P5.2 — check-in questions (design PDF page 9).
 * NEW path /check-ins/:trackerId/questions (and /check-ins/questions).
 * Replaces, for coaches, the "Forms & questions" step of the old journey
 * editor (/trackers/:id → Edit). The coach only edits questions here — no
 * journey settings. Changes go to a DRAFT; members see them only after
 * Publish (now, when the next check-in opens, or at a set time).
 * The old editor stays as it is.
 * Layout = PDF page 9: form tabs, "Question list" (drag handles, Edit,
 * + Add question), "New question" and "Before publishing" on the right,
 * Save draft / Review changes. "Edit" opens the full question editor.
 */

const TYPE_LABEL = {
  short_text: "Short answer",
  long_text: "Long answer",
  number: "Number",
  scale: "Scale",
  yes_no: "Yes / No",
  date: "Date",
  time: "Time",
  dropdown: "Dropdown",
  multiple_choice: "Multiple choice",
  checkbox: "Checkbox",
  image_upload: "Image upload",
  file_upload: "File upload",
  email: "Email",
  phone: "Phone",
};

const NEW_TYPES = [
  { value: "long_text", label: "Long answer" },
  { value: "short_text", label: "Short answer" },
  { value: "number", label: "Number" },
  { value: "scale", label: "Scale from 1 to 5" },
  { value: "scale10", label: "Scale from 1 to 10" },
  { value: "yes_no", label: "Yes / No" },
  { value: "date", label: "Date" },
];

/** Same shape as trackerStorage.createTrackerSpecificFormQuestion (old editor). */
const newQuestion = ({ label, type, required }, order) => {
  const scale = type === "scale" || type === "scale10";
  return {
    id: `tq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    source: "tracker",
    questionId: null,
    label,
    placeholder: "",
    helpText: "",
    required: Boolean(required),
    searchable: true,
    filterable: true,
    measurable: false,
    order,
    mode: "general",
    fieldType: scale ? "scale" : type,
    measureType: "",
    measureSeriesKey: "",
    measureRole: "",
    units: [],
    defaultUnit: "",
    measureDirection: "descending",
    scaleMin: 1,
    scaleMax: type === "scale" ? 5 : 10,
    options: [],
  };
};

const describe = (q) => {
  const parts = [];
  const type = TYPE_LABEL[q.fieldType] || (q.fieldType ? String(q.fieldType).replace(/_/g, " ") : "Library question");
  parts.push(type);
  if (q.fieldType === "scale") parts.push(`${q.scaleMin ?? 1} to ${q.scaleMax ?? 10}`);
  if (q.defaultUnit) parts.push(q.defaultUnit);
  parts.push(q.required ? "Required" : "Optional");
  return parts.join(" · ");
};

const ROLE_LABEL = {
  first_submission: "First submission",
  follow_up: "Follow-up",
  final: "Final",
  custom: "Custom",
  daily_check_in: "Daily check-in",
};

const plain = (v) => String(v || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

function ChangeList({ changes }) {
  if (!changes || !changes.count) {
    return <Typography sx={{ fontSize: 13.5, color: CW.muted }}>No changes from the published questions.</Typography>;
  }
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {changes.forms.map((form) => (
        <Box key={form.formId}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mb: 0.5 }}>{form.formName}</Typography>
          {form.added.map((q, i) => (
            <Typography key={`a${i}`} sx={{ fontSize: 13, color: CW.green }}>
              + Added “{q.label}”{q.required ? " (required)" : ""}
            </Typography>
          ))}
          {form.removed.map((q, i) => (
            <Typography key={`r${i}`} sx={{ fontSize: 13, color: CW.red }}>
              − Removed “{q.label}”
            </Typography>
          ))}
          {form.changed.map((q, i) => (
            <Typography key={`c${i}`} sx={{ fontSize: 13, color: CW.amber }}>
              ~ Changed “{q.from || q.label}”{q.from && q.from !== q.label ? ` → “${q.label}”` : ""} ({q.fields.join(", ")})
            </Typography>
          ))}
          {form.reordered ? <Typography sx={{ fontSize: 13, color: CW.muted }}>↕ Order changed</Typography> : null}
          {form.renamed ? <Typography sx={{ fontSize: 13, color: CW.muted }}>✎ Form name / description changed</Typography> : null}
        </Box>
      ))}
    </Box>
  );
}

export default function CoachQuestionsPage() {
  const { trackerId: routeTrackerId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [journeys, setJourneys] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [forms, setForms] = useState([]); // working copy (draft or published)
  const [dirty, setDirty] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [when, setWhen] = useState("next");
  const [at, setAt] = useState("");
  const [formId, setFormId] = useState("");
  const [newQ, setNewQ] = useState({ label: "", type: "long_text", required: false });
  const [dragIndex, setDragIndex] = useState(null);

  const trackerId = routeTrackerId || "";

  useEffect(() => {
    getTrackersApi({ limit: 200, sortBy: "name", sortDir: "asc" }).then((result) => {
      const list = (result?.trackers || []).filter((t) => t.status !== "archived");
      setJourneys(list);
      if (!routeTrackerId && list[0]?._id) navigate(`/check-ins/${list[0]._id}/questions`, { replace: true });
    });
  }, [routeTrackerId, navigate]);

  const apply = (result) => {
    setData(result);
    setForms(JSON.parse(JSON.stringify(result.draft ? result.draft.forms : result.forms)));
    setDirty(false);
  };

  const load = useCallback(async () => {
    if (!trackerId) return;
    setError("");
    const result = await getQuestionsApi(trackerId);
    if (result?.code === 200) apply(result);
    else setError(result?.message || "Could not load questions");
  }, [trackerId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const editingForm = useMemo(() => forms.find((f) => String(f.id) === String(editingId)) || null, [forms, editingId]);

  const onFormChange = (patch) => {
    setForms((prev) => prev.map((f) => (String(f.id) === String(editingId) ? { ...f, ...patch, role: f.role } : f)));
    setDirty(true);
  };

  const saveDraft = async (quiet) => {
    setBusy(true);
    const result = await saveQuestionDraftApi(trackerId, forms);
    setBusy(false);
    if (result?.code === 200) {
      apply(result);
      if (!quiet) enqueueSnackbar(result.message || "Draft saved", { variant: "success" });
      return result;
    }
    enqueueSnackbar(result?.message || "Draft not saved", { variant: "error" });
    return null;
  };

  const discard = async () => {
    setBusy(true);
    const result = await discardQuestionDraftApi(trackerId);
    setBusy(false);
    if (result?.code === 200) {
      apply(result);
      enqueueSnackbar("Draft discarded", { variant: "success" });
    } else enqueueSnackbar(result?.message || "Could not discard", { variant: "error" });
  };

  const openReview = async () => {
    if (dirty || !data?.draft) {
      const saved = await saveDraft(true);
      if (!saved) return;
    }
    const next = data?.impact?.nextWindowOpensAt;
    setWhen(next ? "next" : "now");
    setAt(moment().add(1, "day").startOf("hour").format("YYYY-MM-DDTHH:mm"));
    setReviewOpen(true);
  };

  const publish = async () => {
    const next = data?.impact?.nextWindowOpensAt;
    const body =
      when === "now"
        ? { when: "now" }
        : { when: "scheduled", at: when === "next" ? next : moment(at).toISOString() };
    setBusy(true);
    const result = await publishQuestionsApi(trackerId, body);
    setBusy(false);
    if (result?.code === 200) {
      apply(result);
      setReviewOpen(false);
      enqueueSnackbar(result.message || "Published", { variant: "success" });
    } else enqueueSnackbar(result?.message || "Could not publish", { variant: "error" });
  };

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!trackerId || !data) return <Loading />;

  const draft = data.draft;
  const impact = data.impact || {};
  const changes = draft?.changes;
  const preferred = forms.find((f) => f.role === "follow_up") || forms[0];
  const form = forms.find((f) => String(f.id) === String(formId)) || preferred || null;
  const questions = form?.questions || [];
  const hasChanges = Boolean(dirty || draft);

  const setQuestions = (next) => {
    setForms((prev) => prev.map((f) => (String(f.id) === String(form.id) ? { ...f, questions: next.map((q, order) => ({ ...q, order })) } : f)));
    setDirty(true);
  };

  const addQuestion = () => {
    const label = newQ.label.trim();
    if (!label || !form) return;
    setQuestions([...questions, newQuestion({ ...newQ, label }, questions.length)]);
    setNewQ({ label: "", type: newQ.type, required: false });
    enqueueSnackbar("Question added to the draft list", { variant: "success" });
  };

  const moveQuestion = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...questions];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setQuestions(next);
  };

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: 6 }}>
        <Breadcrumbs
          items={[
            { label: "Check-ins", onClick: () => navigate("/check-ins/planner") },
            { label: data.tracker?.name || "Tracker settings" },
            { label: "Questions" },
          ]}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "flex-start", mt: 1, mb: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            <PageTitle>
              {form?.name ? `${form.name} questions` : "Check-in questions"}
              <InfoHint id="questions.header" />
            </PageTitle>
            <Typography sx={{ fontSize: { xs: 14, md: 16 }, color: CW.muted, mt: 0.75 }}>
              {data.tracker?.name}
              {` · ${impact.activePeople || 0} active enrolment${impact.activePeople === 1 ? "" : "s"}`}
              {impact.nextWindowOpensAt ? ` · next check-in opens ${moment(impact.nextWindowOpensAt).format("ddd D MMM, HH:mm")}` : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 1.25 }}>
            {hasChanges ? (
              <Pill tone="amber" sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
                {dirty ? "Unsaved changes" : draft?.publishAt ? `Publishes ${moment(draft.publishAt).format("ddd D MMM, HH:mm")}` : "Draft changes"}
              </Pill>
            ) : (
              <Pill tone="green" sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
                Published · version {data.version}
              </Pill>
            )}
            <TextField
              select
              size="small"
              value={trackerId}
              onChange={(e) => {
                if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
                navigate(`/check-ins/${e.target.value}/questions`);
              }}
              inputProps={{ "aria-label": "Journey" }}
              sx={{ minWidth: 240, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              {journeys.map((t) => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name}
                </MenuItem>
              ))}
              {!journeys.some((t) => t._id === trackerId) ? <MenuItem value={trackerId}>{data.tracker?.name}</MenuItem> : null}
            </TextField>
          </Box>
        </Box>

        {/* Form tabs */}
        <Panel info="questions.tabs" sx={{ mb: 2.5, pt: 1.5, pb: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
            <UnderlineTabs
              value={form ? String(form.id) : ""}
              onChange={(v) => setFormId(v)}
              sx={{ borderBottom: 0, flex: 1, minWidth: 0 }}
              options={forms.map((f) => ({ value: String(f.id), label: f.name || ROLE_LABEL[f.role] || "Form" }))}
            />
            <Typography sx={{ fontSize: 13, color: CW.muted, pb: 1.25 }}>
              Coaches see and edit the same questions · Journey settings are in the journey editor
            </Typography>
          </Box>
        </Panel>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 460px" }, gap: 2.5, alignItems: "start" }}>
          {/* Question list */}
          <Panel
            title="Question list"
            info="questions.list"
            sx={{ p: { xs: 2, md: 3 } }}
            action={<Typography sx={{ fontSize: 13, color: CW.muted }}>Reorder using the handles</Typography>}
          >
            {!form ? (
              <Typography sx={{ fontSize: 13.5, color: CW.muted }}>This journey has no forms.</Typography>
            ) : questions.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: CW.muted }}>No form-specific questions (shared questions still show).</Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {questions.map((q, i) => (
                  <Box
                    key={q.id || i}
                    data-testid="question-row"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      moveQuestion(dragIndex, i);
                      setDragIndex(null);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      bgcolor: CW.soft,
                      borderRadius: "10px",
                      p: { xs: 1.5, md: 2 },
                      outline: dragIndex === i ? `2px dashed ${CW.primary}` : "none",
                    }}
                  >
                    <Box
                      draggable
                      onDragStart={(e) => {
                        setDragIndex(i);
                        if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setDragIndex(null)}
                      title="Drag to reorder"
                      aria-label={`Move ${plain(q.label) || "question"}`}
                      sx={{ cursor: "grab", color: CW.muted, fontSize: 18, letterSpacing: 2, userSelect: "none", px: 0.5 }}
                    >
                      ⋮⋮
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: CW.ink }} noWrap>
                        {plain(q.label) || "(library question)"}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: CW.muted, mt: 0.25 }}>{describe(q)}</Typography>
                    </Box>
                    <TextLink onClick={() => setEditingId(form.id)} sx={{ fontSize: 14.5 }}>
                      Edit
                    </TextLink>
                  </Box>
                ))}
              </Box>
            )}
            {form ? (
              <button
                type="button"
                className="small-contained-outlined-button"
                style={{ width: "100%", padding: "12px 16px", marginTop: 24, color: CW.ink, borderColor: CW.line }}
                onClick={() => {
                  const el = document.getElementById("cw-new-question");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.focus();
                  }
                }}
              >
                + Add question
              </button>
            ) : null}
            <Typography sx={{ fontSize: 13, color: CW.muted, mt: 2 }}>
              Edits apply to future forms after you review and publish the changes.
              {form ? (
                <>
                  {" "}
                  <TextLink onClick={() => setEditingId(form.id)} sx={{ fontSize: 13 }}>
                    Open the full editor
                  </TextLink>{" "}
                  for options, units and library questions.
                </>
              ) : null}
            </Typography>
          </Panel>

          <Box sx={{ display: "grid", gap: 2.5, minWidth: 0 }}>
            {/* New question */}
            <Panel title="New question" info="questions.new" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.muted, mb: 0.75 }}>Question wording</Typography>
              <TextField
                id="cw-new-question"
                fullWidth
                multiline
                minRows={2}
                value={newQ.label}
                onChange={(e) => setNewQ({ ...newQ, label: e.target.value })}
                placeholder="e.g. How manageable did your nutrition plan feel this week?"
                inputProps={{ maxLength: 300, "aria-label": "Question wording" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft } }}
              />
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.muted, mt: 2, mb: 0.75 }}>Answer type</Typography>
              <TextField
                select
                fullWidth
                value={newQ.type}
                onChange={(e) => setNewQ({ ...newQ, type: e.target.value })}
                inputProps={{ "aria-label": "Answer type" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              >
                {NEW_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={newQ.required} onChange={(e) => setNewQ({ ...newQ, required: e.target.checked })} />}
                  label={<Typography sx={{ fontSize: 14, color: CW.muted }}>Required</Typography>}
                />
                <button type="button" className="small-contained-button" style={{ padding: "9px 20px" }} disabled={!newQ.label.trim() || !form} onClick={addQuestion}>
                  Add to list
                </button>
              </Box>
            </Panel>

            {/* Before publishing */}
            <Box sx={{ bgcolor: CW.tealSoft, borderRadius: "14px", p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontSize: { xs: 19, md: 23 }, fontWeight: 700, color: CW.ink, mb: 1.5 }}>
                Before publishing
                <InfoHint id="questions.before" />
              </Typography>
              <Typography sx={{ fontSize: 14, color: CW.muted, lineHeight: 1.9 }}>
                Review the changes and affected clients.
                <br />
                Open check-ins keep their current questions.
                <br />
                Previous answers stay in client history.
              </Typography>
              {draft && !dirty && changes?.count ? (
                <Box sx={{ mt: 1.5 }}>
                  <ChangeList changes={changes} />
                </Box>
              ) : null}
              <Box sx={{ borderTop: `2px solid ${CW.primary}`, mt: 2.5, pt: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>Current version: {data.version}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink, mt: 1 }}>
                  Draft version: {hasChanges ? data.version + 1 : "—"}
                </Typography>
                {draft?.updatedAt ? (
                  <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1 }}>
                    Draft saved {moment.min(moment(draft.updatedAt), moment()).fromNow()}
                    {draft.byName ? ` by ${draft.byName}` : ""}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: "space-between", mt: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 13.5, color: CW.muted }}>
              Question changes do not change check-in days or coach configuration.
              <InfoHint id="questions.bottom" />
            </Typography>
            {hasChanges ? (
              <TextLink onClick={() => (draft ? discard() : apply(data))} disabled={busy} sx={{ fontSize: 12.5, color: CW.red, fontWeight: 600 }}>
                Discard draft
              </TextLink>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              style={{ padding: "11px 22px", minWidth: 170, color: CW.ink, borderColor: CW.line }}
              disabled={busy || !dirty}
              onClick={() => saveDraft(false)}
            >
              Save draft
            </button>
            <button
              type="button"
              className="small-contained-button"
              style={{ padding: "11px 22px", minWidth: 240 }}
              disabled={busy || !hasChanges}
              onClick={openReview}
            >
              Review changes
            </button>
          </Box>
        </Box>

        {/* Versions */}
        {(data.versions || []).length ? (
          <Panel title="Version history" info="questions.versions" sx={{ mt: 3 }}>
            {[...data.versions].reverse().map((v) => (
              <Box key={v.version} sx={{ display: "flex", gap: 1.5, py: 0.9, borderTop: `1px solid ${CW.line}`, flexWrap: "wrap" }}>
                <Pill tone={v.version === data.version ? "green" : "muted"}>v{v.version}</Pill>
                <Typography sx={{ fontSize: 13.5, color: CW.ink }}>
                  {v.publishedAt ? moment(v.publishedAt).format("D MMM YYYY, HH:mm") : "Original"}
                  {v.byName && v.publishedAt ? ` · ${v.byName}` : ""}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: CW.muted }}>
                  {v.questionCount} questions{v.summary?.count ? ` · ${v.summary.count} change${v.summary.count === 1 ? "" : "s"}` : ""}
                </Typography>
              </Box>
            ))}
          </Panel>
        ) : null}
      </Box>

      {/* A global ".MuiDrawer-paperAnchorRight { width: 400px }" rule squeezes the
          editor drawer on this page; two classes win over one. */}
      <GlobalStyles
        styles={{
          ".tracker-fields-drawer.MuiDrawer-paperAnchorRight": { width: "min(1100px, 100vw) !important" },
        }}
      />
      <EditFormFieldsDialog
        open={Boolean(editingId)}
        form={editingForm}
        sharedQuestions={data.sharedQuestions || []}
        personalInfoQuestions={data.personalInfoQuestions || []}
        allForms={forms}
        trackerName={data.tracker?.name}
        onClose={() => setEditingId(null)}
        onChange={onFormChange}
      />

      <Dialog open={reviewOpen} onClose={busy ? undefined : () => setReviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Review changes</DialogTitle>
        <DialogContent dividers>
          <ChangeList changes={changes} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mt: 2.5, mb: 0.5 }}>When should members see them?</Typography>
          <RadioGroup value={when} onChange={(e) => setWhen(e.target.value)}>
            {impact.nextWindowOpensAt ? (
              <FormControlLabel
                value="next"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 13.5 }}>
                    When the next check-in opens ({moment(impact.nextWindowOpensAt).format("ddd D MMM, HH:mm")}) — open check-ins keep the old questions
                  </Typography>
                }
              />
            ) : null}
            <FormControlLabel
              value="now"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: 13.5 }}>
                  Now — {impact.openUnsubmitted || 0} open check-in{impact.openUnsubmitted === 1 ? "" : "s"} not yet submitted will get the new questions
                </Typography>
              }
            />
            <FormControlLabel value="at" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>At a set time</Typography>} />
          </RadioGroup>
          {when === "at" ? (
            <TextField
              size="small"
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
              sx={{ mt: 1, ml: 4 }}
              InputLabelProps={{ shrink: true }}
            />
          ) : null}
          {when === "now" && impact.openUnsubmitted ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              People part-way through an open check-in may see questions change.
            </Alert>
          ) : null}
          <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1.5 }}>
            Submitted check-ins are never changed. The published questions are kept as version {data.version}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <button type="button" className="small-contained-outlined-button" disabled={busy} onClick={() => setReviewOpen(false)}>
            Keep editing
          </button>
          <button type="button" className="small-contained-button" disabled={busy || !changes?.count} onClick={publish}>
            {when === "now" ? "Publish now" : "Schedule publish"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
